/**
 * 이용권 상세 패널 — 탭 구성
 *
 *  이용권 관리 목록(마스터-디테일) 우측 패널.
 *  섹션을 탭으로 분리: 요약 / 사용이력 / 환원이력
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RotateCcw } from 'lucide-react';
import {
  ticketsApi,
  type TicketSummary,
  type TicketUseRow,
  type TicketCancelRow,
  type ReservStatus,
} from '@/services/api';
import {
  Section,
  DescList,
  StatusBadge,
  LoadingState,
  ErrorState,
  EmptyState,
  type BadgeTone,
} from '@/components/common';

const TICKET_STATUS_LABEL: Record<string, string> = {
  ACTIVE: '유효',
  EXHAUSTED: '소진',
  EXPIRED: '만료',
  REVOKED: '회수',
};

const TICKET_STATUS_TONE: Record<string, BadgeTone> = {
  ACTIVE: 'success',
  EXHAUSTED: 'neutral',
  EXPIRED: 'danger',
  REVOKED: 'warning',
};

const PRODUCT_TYPE_LABEL: Record<string, string> = {
  PRODUCT_TYPE_DAYCARE: '데이케어',
  PRODUCT_TYPE_HOTEL: '호텔',
  PRODUCT_TYPE_CLASS: '수업',
  PRODUCT_TYPE_GROOMING: '미용',
  PRODUCT_TYPE_KINDERGARTEN: '유치원',
};

const PAYMENT_TYPE_LABEL: Record<string, string> = {
  ONLINE: '온라인',
  OFFLINE: '오프라인(수동)',
};

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  PENDING: '결제대기',
  PAID: '결제완료(수동발급)',
  COMPLETED: '결제완료',
  REFUND: '결제취소(환불)',
};

const RESERV_TYPE_LABEL: Record<string, string> = {
  RESERV_TYPE_CLASS: '수업',
  RESERV_TYPE_DAYCARE: '데이케어',
  RESERV_TYPE_HOTEL: '호텔',
  RESERV_TYPE_GROOMING: '미용',
  RESERV_TYPE_TRAINING: '훈련',
};

const RESERV_STATUS_LABEL: Record<string, string> = {
  RESERV_STATUS_TEMP: '임시',
  RESERV_STATUS_REQUESTED: '요청',
  RESERV_STATUS_UPDATED: '수정',
  RESERV_STATUS_CONFIRMED: '확정',
  RESERV_STATUS_CANCELED: '취소',
  RESERV_STATUS_COMPLETED: '완료',
};

const RESERV_STATUS_TONE: Record<string, BadgeTone> = {
  RESERV_STATUS_TEMP: 'neutral',
  RESERV_STATUS_REQUESTED: 'info',
  RESERV_STATUS_UPDATED: 'info',
  RESERV_STATUS_CONFIRMED: 'success',
  RESERV_STATUS_CANCELED: 'danger',
  RESERV_STATUS_COMPLETED: 'neutral',
};

type TabKey = 'summary' | 'uses' | 'cancels';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'summary', label: '요약' },
  { key: 'uses', label: '사용이력' },
  { key: 'cancels', label: '환원이력' },
];

/**
 * 잔여 표기 — 호텔 이용권은 박수 누적 차감이 없고 1회 사용 시 전량 소진되므로
 * 잔여 박수를 보여주지 않는다.
 */
function formatRemaining(t: TicketSummary): string {
  // 회수된 이용권은 사용 이력과 무관하게 잔여 없음
  if (t.ticketStatus === 'REVOKED') {
    return '회수됨';
  }
  if (t.productType === 'PRODUCT_TYPE_HOTEL') {
    return t.usedQty > 0 ? '사용 완료' : `${t.splitCount}박 (미사용)`;
  }
  return `${t.remainingQty} / ${t.splitCount}회`;
}

interface TicketDetailPanelProps {
  purchaseTicketSeq: number;
  onClose: () => void;
  /** 결제취소(환불) 모달 열기 — 목록 갱신을 위해 페이지에서 처리한다 */
  onRefund: (seq: number) => void;
}

export function TicketDetailPanel({
  purchaseTicketSeq,
  onClose,
  onRefund,
}: TicketDetailPanelProps) {
  const [tab, setTab] = useState<TabKey>('summary');

  // 이용권이 바뀌면 첫 탭으로 되돌린다
  useEffect(() => {
    setTab('summary');
  }, [purchaseTicketSeq]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['ticketDetail', purchaseTicketSeq],
    queryFn: () => ticketsApi.getTicketDetail(purchaseTicketSeq),
    enabled: !!purchaseTicketSeq,
  });

  if (isLoading) {
    return (
      <div className="flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
        <LoadingState message="이용권 상세를 불러오는 중..." />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
        <ErrorState error={error ?? '이용권 정보를 찾을 수 없습니다.'} onRetry={() => refetch()} />
      </div>
    );
  }

  const { summary, uses, cancels } = data;
  const status = TICKET_STATUS_TONE[summary.ticketStatus] ?? 'neutral';

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border-2 border-primary-200 bg-white">
      {/* ── 헤더 ── */}
      <div className="shrink-0 border-b border-gray-200 px-5 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-base font-bold text-gray-900">{summary.name}</h2>
              <StatusBadge tone={status}>
                {TICKET_STATUS_LABEL[summary.ticketStatus] ?? summary.ticketStatus}
              </StatusBadge>
              <span className="text-xs font-normal text-gray-500">
                #{summary.purchaseTicketSeq}
              </span>
            </div>
            <p className="mt-0.5 truncate text-xs text-gray-500">
              {summary.companyName} · {summary.userName}
              {summary.petName ? ` / ${summary.petName}` : ''} · {summary.orderId}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {summary.refundable && (
              <button
                type="button"
                onClick={() => onRefund(summary.purchaseTicketSeq)}
                className="inline-flex items-center gap-1 rounded-md border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
              >
                <RotateCcw size={12} />
                결제취소
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-gray-500 hover:text-gray-900"
            >
              닫기 ×
            </button>
          </div>
        </div>
      </div>

      {/* ── 탭 바 ── */}
      <div className="shrink-0 border-b border-gray-200 bg-gray-50 px-3">
        <div className="flex gap-1">
          {TABS.map(t => {
            const active = tab === t.key;
            const count = t.key === 'uses' ? uses.length : t.key === 'cancels' ? cancels.length : 0;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`-mb-px shrink-0 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                  active
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {t.label}
                {t.key !== 'summary' && count > 0 && (
                  <span
                    className={`ml-1 text-xs tabular-nums ${
                      active ? 'text-primary-500' : 'text-gray-400'
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── 탭 본문 (내부 스크롤) ── */}
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-gray-50 p-4">
        {tab === 'summary' && (
          <>
            <Section title="이용 현황">
              <div className="mb-3 flex items-end justify-between gap-4">
                <div>
                  <div className="text-[11px] text-gray-500">잔여</div>
                  <div
                    className={`mt-0.5 text-2xl font-bold leading-none ${
                      summary.ticketStatus === 'REVOKED' ? 'text-amber-600' : 'text-primary-600'
                    }`}
                  >
                    {formatRemaining(summary)}
                  </div>
                </div>
                <div className="flex gap-4 text-right">
                  <div>
                    <div className="text-[10px] text-gray-500">사용</div>
                    <div className="text-sm font-bold leading-none text-gray-900">
                      {summary.usedQty}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-500">환원</div>
                    <div className="text-sm font-bold leading-none text-gray-900">
                      {summary.canceledQty}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-500">총</div>
                    <div className="text-sm font-bold leading-none text-gray-900">
                      {summary.splitCount}
                    </div>
                  </div>
                </div>
              </div>
              <DescList
                cols={2}
                items={[
                  {
                    label: '유효기간',
                    value: `${(summary.dtUsable ?? '').slice(0, 10)} ~ ${(
                      summary.dtExpired ?? ''
                    ).slice(0, 10)}`,
                  },
                  {
                    label: summary.ticketStatus === 'REVOKED' ? '회수일시' : '만료까지',
                    value:
                      summary.ticketStatus === 'REVOKED'
                        ? summary.revokedAt ?? '회수됨 (일시 미기록)'
                        : summary.ticketStatus === 'EXPIRED'
                          ? '만료됨'
                          : `D-${summary.daysToExpire}`,
                  },
                  {
                    label: '상품 유형',
                    value: PRODUCT_TYPE_LABEL[summary.productType] ?? summary.productType,
                  },
                  { label: '다회권', value: summary.splitYn === 'Y' ? 'Y' : 'N' },
                ]}
              />
            </Section>

            <Section title="구매/결제 정보">
              <DescList
                cols={2}
                items={[
                  { label: '회원', value: `${summary.userName} (${summary.userPhone ?? '-'})` },
                  { label: '반려동물', value: summary.petName ?? '-' },
                  { label: '사업장', value: summary.companyName },
                  { label: '주문번호', value: summary.orderId },
                  {
                    label: '결제 금액',
                    value: `${Number(summary.amount ?? 0).toLocaleString()}원`,
                  },
                  {
                    label: '정가/할인',
                    value: `${Number(summary.cost ?? 0).toLocaleString()}원 / ${
                      summary.discount ?? 0
                    }%`,
                  },
                  {
                    label: '발급 경로',
                    value: PAYMENT_TYPE_LABEL[summary.paymentType] ?? summary.paymentType,
                  },
                  {
                    label: '결제 상태',
                    value: PAYMENT_STATUS_LABEL[summary.paymentStatus] ?? summary.paymentStatus,
                  },
                  { label: '발급일시', value: summary.dtRegist },
                  {
                    label: '결제취소 가능',
                    value: summary.refundable ? '가능' : summary.refundableReason ?? '불가',
                  },
                ]}
              />
            </Section>
          </>
        )}

        {tab === 'uses' && (
          <Section title="사용 이력" description={`총 ${uses.length}건`}>
            {uses.length === 0 ? (
              <EmptyState message="사용 이력이 없습니다." />
            ) : (
              <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {uses.map(use => (
                  <UseRow
                    key={use.ticketUseSeq}
                    use={use}
                    canceledQty={cancels
                      .filter(c => c.ticketUseSeq === use.ticketUseSeq)
                      .reduce((s, c) => s + c.qty, 0)}
                  />
                ))}
              </div>
            )}
          </Section>
        )}

        {tab === 'cancels' && (
          <Section title="환원 이력" description={`총 ${cancels.length}건`}>
            {cancels.length === 0 ? (
              <EmptyState message="환원 이력이 없습니다." />
            ) : (
              <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {cancels.map(c => (
                  <CancelRow key={c.ticketCancelSeq} cancel={c} />
                ))}
              </div>
            )}
          </Section>
        )}
      </div>
    </div>
  );
}

/** 사용 1건 — 2줄 압축 (예약 정보는 둘째 줄) */
function UseRow({ use, canceledQty }: { use: TicketUseRow; canceledQty: number }) {
  const r = use.reservation;
  const meta = r
    ? [
        r.companyName,
        RESERV_TYPE_LABEL[r.reservType] ?? r.reservType,
        r.dtExpected,
        `예약#${r.reservationSeq} / 상세#${r.reservDetailSeq}`,
      ]
        .filter(Boolean)
        .join(' · ')
    : '연결된 예약 정보 없음';

  return (
    <div className="px-3 py-2">
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="shrink-0 text-sm font-semibold text-gray-900">{use.dtRegist}</span>
          <span className="shrink-0 text-[11px] text-gray-600">사용 {use.qty}</span>
          {canceledQty > 0 && (
            <span className="shrink-0 rounded bg-amber-50 px-1 text-[10px] text-amber-700">
              환원 {canceledQty}
            </span>
          )}
        </div>
        {r && (
          <StatusBadge tone={RESERV_STATUS_TONE[r.status as ReservStatus] ?? 'neutral'}>
            {RESERV_STATUS_LABEL[r.status] ?? r.status}
          </StatusBadge>
        )}
      </div>
      <div className="mt-0.5 truncate text-[11px] text-gray-400" title={meta}>
        {meta}
      </div>
    </div>
  );
}

/** 환원 1건 */
function CancelRow({ cancel }: { cancel: TicketCancelRow }) {
  return (
    <div className="px-3 py-2">
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="shrink-0 text-sm font-semibold text-gray-900">{cancel.dtRegist}</span>
          <span className="shrink-0 text-[11px] text-gray-600">환원 {cancel.qty}</span>
        </div>
        <span className="shrink-0 text-[10px] text-gray-400">#{cancel.ticketCancelSeq}</span>
      </div>
      <div className="mt-0.5 truncate text-[11px] text-gray-400">
        원 사용 #{cancel.ticketUseSeq}
        {cancel.reservDetailSeq ? ` · 예약상세 #${cancel.reservDetailSeq}` : ''}
      </div>
    </div>
  );
}
